/**
 * CR309: Reusable Asset Picker Modal
 * 
 * Shared picker modal used for Starburst, Watermark, and other asset types.
 * Filters assets by type and allows search by name/tags.
 */

import { useState, useMemo } from "react";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Image, Search, Star, Droplets, X, Flag } from "lucide-react";
import type { AppAsset, AssetType } from "@/lib/asset-studio/types";
import { ASSET_TYPE_LABELS } from "@/lib/asset-studio/types";

// ================================================================
// TYPES
// ================================================================

export interface AssetPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Filter assets by this type */
  filterType: AssetType;
  /** Currently selected asset ID */
  selectedId?: string | null;
  /** Callback when asset is selected */
  onSelect: (asset: { id: string; name: string } | null) => void;
  /** Modal title override */
  title?: string;
  /** Allow clearing selection (show "None" option) */
  allowClear?: boolean;
  /** Available assets (filtered by type externally or passed in) */
  assets: AppAsset[];
  /** Loading state */
  loading?: boolean;
}

// Icon map for asset types
const ASSET_TYPE_ICONS: Partial<Record<AssetType, typeof Star>> = {
  starburst: Star,
  watermark: Droplets,
  ribbon: Flag,
};

// ================================================================
// COMPONENT
// ================================================================

export function AssetPickerModal({
  open,
  onOpenChange,
  filterType,
  selectedId,
  onSelect,
  title,
  allowClear = true,
  assets,
  loading = false,
}: AssetPickerModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingSelection, setPendingSelection] = useState<string | null>(selectedId ?? null);

  // Get icon for type
  const TypeIcon = ASSET_TYPE_ICONS[filterType] || Image;
  const typeLabel = ASSET_TYPE_LABELS[filterType] || filterType;

  // Reset pending selection when modal opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setPendingSelection(selectedId ?? null);
      setSearchQuery("");
    }
    onOpenChange(isOpen);
  };

  // Filter assets by search
  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return assets;
    const q = searchQuery.toLowerCase();
    return assets.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.tags?.some((t) => t.toLowerCase().includes(q))
    );
  }, [assets, searchQuery]);

  const handleConfirm = () => {
    if (pendingSelection === null) {
      onSelect(null);
    } else {
      const asset = assets.find((a) => a.id === pendingSelection);
      if (asset) {
        onSelect({ id: asset.id, name: asset.name });
      }
    }
    onOpenChange(false);
  };

  const handleClear = () => {
    setPendingSelection(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5 text-primary" />
            {title || `Select ${typeLabel}`}
          </DialogTitle>
          <DialogDescription>
            Choose a {typeLabel.toLowerCase()} from your Asset Studio library.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${typeLabel.toLowerCase()}s...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Asset Grid */}
        <ScrollArea className="h-[320px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <p className="text-sm">Loading assets...</p>
            </div>
          ) : filteredAssets.length === 0 && !allowClear ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Image className="h-12 w-12 mb-2 opacity-50" />
              <p className="text-sm">No {typeLabel.toLowerCase()} assets found</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {/* None / Clear option */}
              {allowClear && (
                <button
                  type="button"
                  onClick={handleClear}
                  className={`relative p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                    pendingSelection === null
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-transparent bg-muted/30 hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="w-16 h-16 mx-auto mb-2 flex items-center justify-center bg-muted rounded-lg">
                    <X className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="text-xs font-medium truncate text-center">
                    None
                  </div>
                  <div className="text-[10px] text-muted-foreground text-center mt-1">
                    Use default
                  </div>
                  {pendingSelection === null && (
                    <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </button>
              )}

              {/* Asset items */}
              {filteredAssets.map((asset) => {
                const isSelected = pendingSelection === asset.id;
                const hasDefaultTag = asset.tags?.some(
                  (t) => t.toLowerCase() === "default"
                );
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => setPendingSelection(asset.id)}
                    className={`relative p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-md"
                        : "border-transparent bg-muted/30 hover:border-muted-foreground/30"
                    }`}
                  >
                    {/* Preview */}
                    <img
                      src={getAssetImageSrc(asset) || ""}
                      alt={asset.name}
                      className="w-16 h-16 mx-auto mb-2 object-contain"
                    />

                    {/* Name */}
                    <div className="text-xs font-medium truncate text-center">
                      {asset.name}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 justify-center mt-1">
                      {hasDefaultTag && (
                        <Badge
                          variant="secondary"
                          className="text-[9px] px-1 py-0"
                        >
                          default
                        </Badge>
                      )}
                      {(asset.tags || [])
                        .slice(0, 2)
                        .filter((t) => t.toLowerCase() !== "default")
                        .map((tag) => (
                          <Badge
                            key={tag}
                            variant="outline"
                            className="text-[9px] px-1 py-0"
                          >
                            {tag}
                          </Badge>
                        ))}
                      {(asset.tags?.length || 0) > 3 && (
                        <span className="text-[9px] text-muted-foreground">
                          +{(asset.tags?.length || 0) - 3}
                        </span>
                      )}
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            {pendingSelection === null ? "Clear Selection" : "Use Selected"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
